/**
 * The 24-hour chart in named lanes, one column per recipient-local hour:
 * its hours (a bracket), Score (the formula's bar — coloured in its hours,
 * or the window's on a master config, grey outside; gold 1-2-3 = the day's
 * best hours; the Flow Optimizer's red mark = excluded, not judged yet),
 * Window (the country's), Visits (darker = more), Rules (only when a rule fired — its action's
 * colour and icon), Tags (only when a rule tests an hour tag — the tags each
 * hour carries), Drops (│ planned · ⚡ added by the optimizer today), then
 * the hours on the country's clock and the Danish one. Hover reads an hour,
 * click pins it; bars grow in; reduced motion = static.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { BarChart3, Scale, Send, Sunrise, Users } from 'lucide-react';

import { cn } from '@/lib/utils';

import { RULE_ACTIONS, RULE_TONE_COLOR } from '../../../rules/rule-surfaces';
import { type CountryWindow, dkHour, type HourView } from '../hour-chart-math';
import { hasHourTags, HourTagLane } from '../tags';
import { ChartLane, HOUR_GRID } from './lanes/ChartLane';
import { DropsLane } from './lanes/DropsLane';
import { ScoreColumn } from './lanes/ScoreColumn';
import { SpanBracket } from './lanes/SpanBracket';
import { WindowLane } from './lanes/WindowLane';

interface Props {
  rows: HourView[];
  rankedHours: number;
  startMin: number | null;
  /** The container's end, else its last regular drop. */
  endMin: number | null;
  slotMinutes: number[];
  extras: Array<{ minute: number; kind: 'extra' | 'probe' }>;
  active: number | null;
  pinned: number | null;
  onHover: (hour: number | null) => void;
  onPin: (hour: number) => void;
  timezone: string;
  countryFlag: string;
  /** The country's window — its own lane under the scores. */
  window?: CountryWindow | null;
  /** Hour tag colours (Tag Rules › Hours) for the Tags lane. */
  tagColors?: Record<string, string>;
}

const STRONGEST = ['block', 'no_extra', 'extra_first'];
const firedAction = (row: HourView) => STRONGEST.find((a) => (row.ruleHits ?? []).some((h) => h.state === 'fired' && h.action === a)) ?? null;
const axisVisibility = (h: number) => (h % 6 === 0 ? 'visible' : h % 3 === 0 ? 'invisible sm:visible' : 'invisible lg:visible');

export const HourProfileChart: FC<Props> = (p) => {
  const hasRules = p.rows.some((r) => firedAction(r) !== null);
  const hover = (h: number) => () => p.onHover(h);
  return (
    <div className="flex flex-col gap-1" onMouseLeave={() => p.onHover(null)}>
      <ChartLane><SpanBracket startMin={p.startMin} endMin={p.endMin} /></ChartLane>
      <ChartLane icon={BarChart3} label="Score" title="The formula's score — taller = better. Blue: its hours (on a master config: the window's); grey: the others; gold 1-2-3: the day's best hours." className="h-28">
        <div className="grid h-full" style={HOUR_GRID}>
          {p.rows.map((row) => (
            <ScoreColumn key={row.hour} row={row} rankedHours={p.rankedHours} active={p.active === row.hour} pinned={p.pinned === row.hour} onHover={p.onHover} onPin={p.onPin} />
          ))}
        </div>
      </ChartLane>
      {p.window ? (
        <ChartLane icon={Sunrise} label="Window" title="The country's window (its tile at the top of the country page) — a hard limit: no drop goes outside it.">
          <WindowLane window={p.window} timezone={p.timezone} />
        </ChartLane>
      ) : null}
      <ChartLane icon={Users} label="Visits" title="Unique visits in the first date range — darker = more. An hour a rule excluded (Not enough data, by default) carries the red excluded mark in Score.">
        <div className="grid h-3 gap-px" style={HOUR_GRID}>
          {p.rows.map((row) => (
            <span key={row.hour} onMouseEnter={hover(row.hour)} className={cn('rounded-sm transition-opacity', !row.lit && 'opacity-60')} style={{ background: `hsl(var(--foreground) / ${0.08 + (row.volumePct / 100) * 0.55})` }} />
          ))}
        </div>
      </ChartLane>
      {hasRules ? (
        <ChartLane icon={Scale} label="Rules" title="A rule that fired on the hour — its action's colour; hover for the rule.">
          <div className="grid h-4" style={HOUR_GRID}>
            {p.rows.map((row) => <RuleMark key={row.hour} row={row} onHover={hover(row.hour)} />)}
          </div>
        </ChartLane>
      ) : null}
      {hasHourTags(p.rows) ? <HourTagLane rows={p.rows} colors={p.tagColors} onHover={p.onHover} /> : null}
      <ChartLane icon={Send} label="Drops" title="│ a planned drop · ⚡ a drop the optimizer adds with these settings (live) — hover one for its exact time" className="h-4 border-t border-border">
        <DropsLane slotMinutes={p.slotMinutes} extras={p.extras} timezone={p.timezone} countryFlag={p.countryFlag} onHover={p.onHover} />
      </ChartLane>
      {/* Danish first (15/10/2026): the Danish hour row leads; the columns stay the recipients' hours. */}
      <ChartLane label="🇩🇰" title="The same hours in Danish time, today">
        <HourRow rows={p.rows} active={p.active} label={(h) => dkHour(h, p.timezone)} />
      </ChartLane>
      <ChartLane label={p.countryFlag} title={`${p.timezone} — the recipients' clock (the columns)`}>
        <HourRow rows={p.rows} active={p.active} label={(h) => String(h).padStart(2, '0')} muted />
      </ChartLane>
    </div>
  );
};

const RuleMark: FC<{ row: HourView; onHover: () => void }> = ({ row, onHover }) => {
  const action = firedAction(row);
  const meta = action ? RULE_ACTIONS[action] : null;
  if (!meta) return <span onMouseEnter={onHover} />;
  const Icon = meta.icon;
  const fired = (row.ruleHits ?? []).filter((h) => h.state === 'fired');
  return (
    <span onMouseEnter={onHover} title={fired.map((h) => `${h.name}: ${h.reason}`).join('\n')} className="mx-auto flex h-4 w-[70%] items-center justify-center rounded animate-in fade-in-0 zoom-in-75" style={{ background: RULE_TONE_COLOR[meta.tone] }}>
      <Icon className="h-2.5 w-2.5 text-white" aria-label={meta.badge} />
    </span>
  );
};

const HourRow: FC<{ rows: HourView[]; active: number | null; label: (hour: number) => string; muted?: boolean }> = ({ rows, active, label, muted }) => (
  <div className={cn('grid font-mono text-[10px] leading-4', muted ? 'text-muted-foreground/70' : 'text-muted-foreground')} style={HOUR_GRID}>
    {rows.map((row) => (
      <span key={row.hour} className={cn('text-center transition-colors', active === row.hour ? 'visible font-bold text-foreground' : axisVisibility(row.hour))}>
        {label(row.hour)}
      </span>
    ))}
  </div>
);
